import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { useApiConnected, usePlatformNotifications, useProjects } from '../../services/api';
import { useQueryClient } from '@tanstack/react-query';
import { useRuntimeEventStore } from '../../store/runtimeEventStore';
import { APP_VERSION } from '../../buildInfo';
import { SideTip } from './SideTip';
import { useAuth } from '../../auth/AuthContext';
import { AlertCircle, ArrowRight, CheckCheck, CheckCircle2, Info } from 'lucide-react';
import './aurora-chrome.css';
import './notifications-aurora.css';

/** Splits "[TAG] PROJECT - Title | service: message" into parts; falls back to the raw text. */
function parseNotification(text: string): { tag: string; title: string; svc: string; msg: string } {
  const m = /^\[([^\]]+)\]\s*(?:[^-|]+?\s-\s)?([^|]+?)\s*(?:\|\s*([^:]+?):\s*([\s\S]*))?$/.exec(text);
  if (!m) return { tag: '', title: text, svc: '', msg: '' };
  return { tag: m[1], title: m[2], svc: m[3] || '', msg: m[4] || '' };
}

const UI_VERSION_LABEL = /^v/i.test(APP_VERSION) ? APP_VERSION : `v${APP_VERSION}`;

type ThemeMode = 'system' | 'light' | 'dark';
const THEME_ORDER: ThemeMode[] = ['system', 'light', 'dark'];
const THEME_ICONS: Record<ThemeMode, React.ReactNode> = {
  system: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5a8.5 8.5 0 0 1 0 17Z" fill="currentColor" stroke="none" />
    </svg>
  ),
  light: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M4.6 4.6 6 6M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" />
    </svg>
  ),
};

interface SidebarAccountBarProps {
  collapsed?: boolean;
}

/**
 * Sidebar footer: account card with connection status, plus theme, refresh
 * and notification actions.
 */
export const SidebarAccountBar: React.FC<SidebarAccountBarProps> = ({ collapsed = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, setMode } = useAppStore();
  const { user, logout } = useAuth();
  const { notifications, markAsRead, clearAll: clearNotifications } = usePlatformNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [refreshing, setRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const { connected, health } = useApiConnected();
  const { projects } = useProjects();
  const wsConnected = useRuntimeEventStore((s) => s.connected);
  const live = wsConnected || connected;
  const statusTitle = [
    wsConnected ? 'Live updates on' : connected ? 'Live updates off' : 'Offline',
    health?.version ? `API v${health.version}` : null,
    `UI ${UI_VERSION_LABEL}`,
    `${projects.length} project${projects.length === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const unreadCount = notifications.filter((n) => !n.read).length;
  const displayName = user?.name || 'Operator';
  const displayEmail = user?.email || '';
  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('') || 'U';

  const popoverAnchor = {
    vertical: 'top' as const,
    horizontal: 'right' as const,
  };
  const popoverTransform = {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login', { replace: true });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await queryClient.refetchQueries({ type: 'active' });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <div className="acct">
        <SideTip title={displayName} show={collapsed}>
          <button
            type="button"
            className="acct-top"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <span className="avatar">
              {user?.picture ? <img src={user.picture} alt="" /> : <span className="ini">{initials}</span>}
              <i className={`online${live ? '' : ' off'}`} aria-hidden="true" />
            </span>
            <span className="acct-txt">
              <b>{displayName}</b>
              <span className={`st${live ? '' : ' off'}`} title={statusTitle} role="status" aria-live="polite">
                <i />
                <span>{live ? 'Connected' : 'Offline'}</span>
              </span>
            </span>
            <span className="chev" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="m8 9 4-4 4 4M8 15l4 4 4-4" />
              </svg>
            </span>
          </button>
        </SideTip>
        <div className="tools" role="group" aria-label="Quick actions">
          <SideTip title={`Theme: ${mode}`} placement={collapsed ? 'right' : 'top'}>
            <button
              type="button"
              className="ib"
              aria-label={`Theme: ${mode}`}
              onClick={() => setMode(THEME_ORDER[(THEME_ORDER.indexOf(mode) + 1) % THEME_ORDER.length])}
            >
              {THEME_ICONS[mode]}
            </button>
          </SideTip>
          <SideTip title="Refresh" placement={collapsed ? 'right' : 'top'}>
            <button
              type="button"
              className={`ib${refreshing ? ' busy' : ''}`}
              aria-label="Refresh"
              disabled={refreshing}
              onClick={handleRefresh}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 11a8 8 0 0 0-14.5-4.3L4 8.5" />
                <path d="M4 4v4.5h4.5" />
                <path d="M4 13a8 8 0 0 0 14.5 4.3L20 15.5" />
                <path d="M20 20v-4.5h-4.5" />
              </svg>
            </button>
          </SideTip>
          <SideTip
            title={unreadCount ? `Notifications · ${unreadCount} unread` : 'Notifications'}
            placement={collapsed ? 'right' : 'top'}
          >
            <button
              type="button"
              className="ib"
              aria-label={`Notifications, ${unreadCount} unread`}
              onClick={(e) => setNotifAnchor(e.currentTarget)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 17.5h12l-1.5-2V11a4.5 4.5 0 0 0-9 0v4.5L6 17.5Z" />
                <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
              </svg>
              {unreadCount > 0 ? <span className="pip" aria-hidden="true" /> : null}
            </button>
          </SideTip>
        </div>
      </div>

      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        aria-labelledby="sidebar-notifications-title"
        anchorOrigin={popoverAnchor}
        transformOrigin={popoverTransform}
        slotProps={{
          paper: {
            className: 'nt-panel',
            sx: { ml: 1, zIndex: theme.zIndex.modal, backgroundImage: 'none' },
          },
        }}
      >
        <div className="nt-head">
          <h2 id="sidebar-notifications-title">Notifications</h2>
          {unreadCount > 0 && <span className="nt-count">{unreadCount} new</span>}
          <button
            type="button"
            className="nt-mark"
            disabled={unreadCount === 0}
            onClick={() => {
              clearNotifications();
              setNotifAnchor(null);
            }}
          >
            <CheckCheck size={14} aria-hidden="true" />
            Mark all as read
          </button>
        </div>
        <ul className="nt-list">
          {notifications.length === 0 ? (
            <li className="nt-empty">
              <b>You&apos;re all caught up.</b>
              No new notifications right now.
            </li>
          ) : (
            notifications.slice(0, 10).map((notif) => {
              const parts = parseNotification(notif.message);
              const Icon = notif.type === 'success' ? CheckCircle2 : notif.type === 'info' ? Info : AlertCircle;
              return (
                <li key={notif.id}>
                  <button
                    type="button"
                    className={`nt-item ${notif.type}${notif.read ? '' : ' unread'}`}
                    title={notif.message}
                    onClick={() => {
                      markAsRead(notif.id);
                      if (notif.href) {
                        navigate(notif.href);
                        setNotifAnchor(null);
                      } else if (notif.projectId) {
                        navigate(`/projects/${notif.projectId}`);
                        setNotifAnchor(null);
                      }
                    }}
                    aria-label={`Open notification: ${notif.message}`}
                  >
                    <span className="ic" aria-hidden="true">
                      <Icon size={14} />
                    </span>
                    <span className="nt-body">
                      <span className="nt-meta">
                        {parts.tag ? <span className="nt-tag">{parts.tag}</span> : null}
                        {parts.svc ? <span className="nt-svc">{parts.svc}</span> : null}
                        <span className="nt-time">{new Date(notif.timestamp).toLocaleString()}</span>
                      </span>
                      <span className="nt-title">{parts.title}</span>
                      {parts.msg ? <span className="nt-msg">{parts.msg}</span> : null}
                    </span>
                    {notif.read ? null : <span className="nt-dot" aria-hidden="true" />}
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <div className="nt-foot">
          <button
            type="button"
            className="nt-all"
            onClick={() => {
              setNotifAnchor(null);
              navigate('/notifications');
            }}
          >
            View all notifications
            <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      </Popover>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={popoverTransform}
        anchorOrigin={popoverAnchor}
        slotProps={{
          paper: { className: 'acct-menu', sx: { ml: 1, zIndex: theme.zIndex.modal } },
        }}
      >
        <div className="acct-who">
          <span className="avatar sm" aria-hidden="true">
            {user?.picture ? <img src={user.picture} alt="" /> : <span className="ini">{initials}</span>}
          </span>
          <span className="acct-who-txt">
            <b>{displayName}</b>
            {displayEmail && <span>{displayEmail}</span>}
          </span>
        </div>
        <div className="acct-sep" />
        <MenuItem
          className="mi"
          onClick={() => {
            setAnchorEl(null);
            navigate('/settings/profile');
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="8" r="3.5" />
            <path d="M5 20a7 7 0 0 1 14 0" />
          </svg>
          Profile
        </MenuItem>
        <MenuItem
          className="mi"
          onClick={() => {
            setAnchorEl(null);
            navigate('/settings');
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
          </svg>
          Settings
        </MenuItem>
        <div className="acct-sep" />
        <MenuItem className="mi danger" onClick={handleLogout}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h11" />
          </svg>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};

export default SidebarAccountBar;
